import React, { EffectCallback, Fragment, ReactElement, useEffect, useMemo } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '../../../lib/wrappers/Form';
import InstructionalText from '../../../components/ssap/notifications/InstructionalText';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../../../components/ssap/notifications/ValidationErrorMessage';

// Action creators

import { changeData, clearMethods, setMethods } from '../../../actions/ssap/ssapActionCreators';
import { showModal } from '../../../actions/common/actionCreators';

// Utils

import { cloneObject } from '../../../utils/miscellaneous/cloneObject';
import { HouseholdMembersSynchronizer } from '../../../utils/miscellaneous/HouseholdMembersSynchronizer';
import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '../../../utils/tools/preventerTools';
import {
    addToDateWithFormat,
    convertDateToUtc,
    subtractFromDateWithFormat,
} from '../../../utils/dates/date.utils';
import { pick } from '../../../utils/miscellaneous/objectHelpers';

// Constants

import { DATE_FORMAT2 } from '../../../utils/dates/dateMethods';
import { DROPDOWN_OPTIONS } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '../../../constants/ssap/initialValues/emptyObjects';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { ssapModalTypes as MODAL_TYPES } from '../../../constants/ssap/modals/ssapModalTypes';
import { VALIDATION_ERROR_MESSAGES } from '../../../constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';

// Types

import {
    ApplicationStore,
    HouseholdMember,
    HouseholdMembers,
    HraDetails,
    Income,
    IncomeType,
    OnChangeArgTypes,
    SsapPage,
    SsapReduxActions
} from '../../../components/ssap/SsapTypes';

interface HraEntry {
    applicantGuid: string,
    employerName: string,
    isChecked: boolean,
    memberName: string
}

const Page4_67_E5 = (props: SsapPage): ReactElement => {

    // From Redux Store

    const {
        data,
        environment: {
            localDates: {
                serverDate
            }
        },
        form,
        householdMembers,
        page
    } = props;

    // From PagesContainer

    const {
        buttonContainer: ButtonContainer,
        currentHhm,
        formName,
        hhmIndex: member
    } = props;

    const {
        name,
        healthCoverage: {
            offeredIchraIndicator
        },
        healthReimbursementArrangement
    } = currentHhm;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const propsToSync = [
        'cityName',
        'email',
        'employerIdentificationNumber',
        'employerPhoneNumber',
        'name',
        'stateCode',
        'streetName1',
        'streetName2',
        'zipCode'
    ];

    const { date1, date2 } = useMemo(
        () => {
            const date1 = subtractFromDateWithFormat(60, 'day', DATE_FORMAT2, convertDateToUtc(serverDate));

            const date2 = addToDateWithFormat(60, 'day', DATE_FORMAT2, convertDateToUtc(serverDate))

            return {
                date1,
                date2
            };
        },
          [ serverDate ]
    );

    const {
        isHraExtended,
        isInformToAddIncomeModalIsShown
    } = useMemo(
        (): { isHraExtended: boolean, isInformToAddIncomeModalIsShown: boolean } => {
            const trackedIncomeSources: Array<Income> = householdMembers
                .reduce(
                    (trackedIncomeSources: Array<Income>, hhm: HouseholdMember): Array<Income> => {
                        const { incomes } = hhm;
                        return [
                            ...trackedIncomeSources,
                            ...incomes.filter(
                                (income: Income): boolean =>
                                    income.type === IncomeType.JOB || income.type === IncomeType.UNEMPLOYMENT
                            )
                        ]
                    },
                    [ ]
                );

            return {
                isHraExtended: !!offeredIchraIndicator && trackedIncomeSources.length > 0,
                isInformToAddIncomeModalIsShown: !!offeredIchraIndicator && trackedIncomeSources.length === 0
            }
        },
        [ offeredIchraIndicator ]
    );

    useEffect(() => {
        if(isInformToAddIncomeModalIsShown) {
            props.actions.showModal({
                modalType: MODAL_TYPES.INFORM_TO_ADD_INCOME_SOURCE_MODAL,
                modalData: {
                    data: cloneObject(data),
                    index: member,
                    callback: props.actions.changeData
                }
            });
        }
    }, [ isInformToAddIncomeModalIsShown ]);

    const onFieldChange = (...args: OnChangeArgTypes): void => {

        const [ newData, , , [ , value ] ] = args;

        const newState = { ...data };

        newState.taxHousehold.householdMember[member] = newData;

        if (!value) {
            newState.taxHousehold.householdMember[member].healthReimbursementArrangement = [];
        }

        props.actions.changeData(newState);
    };

    const onFieldChange_checkbox = (...args: OnChangeArgTypes): void => {
        const [ , [ applicantGuidFormInput, employerNameFromInput, idxInHra ] ] = args;

        const newState = { ...data };

        const isRecordInHraArray: boolean = !!newState
            .taxHousehold
            .householdMember[member]
            .healthReimbursementArrangement.find((hra: HraDetails) => {
                const {
                    applicantGuid,
                    employerName
                } = hra;

                return applicantGuid === applicantGuidFormInput && employerName === employerNameFromInput;
            });

        if(isRecordInHraArray) {
            newState
                .taxHousehold
                .householdMember[member]
                .healthReimbursementArrangement =
                    newState
                        .taxHousehold
                        .householdMember[member]
                        .healthReimbursementArrangement
                        .filter((hra: HraDetails) => {
                            const {
                                applicantGuid,
                                employerName
                            } = hra;

                            return !(applicantGuid === applicantGuidFormInput && employerName === employerNameFromInput);
                        });
        } else {
            newState
                .taxHousehold
                .householdMember[member]
                .healthReimbursementArrangement = [
                    ...newState.taxHousehold.householdMember[member].healthReimbursementArrangement,
                    {
                        ...cloneObject(EMPTY_OBJECTS.HRA_DETAILS),
                        applicantGuid: applicantGuidFormInput,
                        employerName: employerNameFromInput
                    }
                ];

            const synchronizer = new HouseholdMembersSynchronizer(
                newState
                    .taxHousehold
                    .householdMember
            );

            synchronizer.pullHraData(
                propsToSync,
                {
                    applicantGuid: applicantGuidFormInput,
                    employerName: employerNameFromInput
                },
                member,
                idxInHra
            );

            newState.taxHousehold.householdMember = synchronizer.getSyncedMembers();
        }

        props.actions.changeData(newState);
    };

    const onFieldChange_HraInfo = (...args: OnChangeArgTypes): void => {
        let [ updatedHraDetails, hraIndex, , [ name, , , hraType ] ] = args;

        const newState = { ...data };

        if (name === 'hraType') {
            updatedHraDetails = {
                ...updatedHraDetails,
                // @ts-ignore
                [ name ]: hraType
            };
        }

        const {
            applicantGuid,
            employerName
        } = updatedHraDetails;

        const partToSync = pick(
            updatedHraDetails,
            propsToSync
        );

        newState
            .taxHousehold
            .householdMember[member]
            .healthReimbursementArrangement[hraIndex] = updatedHraDetails;


        const synchronizer = new HouseholdMembersSynchronizer(householdMembers);
        synchronizer.syncHraCommonData(partToSync, { applicantGuid, employerName });

        newState.taxHousehold.householdMember = synchronizer.getSyncedMembers();

        props.actions.changeData(newState);
    };

    const handleAddEmployerDetailsClick = (...args: [HouseholdMembers, HraEntry, number]): void => {

        const [
            householdMembers,
            {
                applicantGuid,
                employerName
            },
            idxInHra
        ] = args;

        const hraDetails = householdMembers[member].healthReimbursementArrangement
            .find((hra: HraDetails) => hra.employerName === employerName && hra.applicantGuid === applicantGuid);

        props.actions.showModal({
            modalType: MODAL_TYPES.ADD_HRA_DETAIL_MODAL,
            modalData: {
                actions: {
                    updateHraDetails: (...args: any[]) => onFieldChange_HraInfo.apply(null, args)
                },
                hraDetails,
                idxInHra,
                pageToReturn: page
            }
        });
    };

    useEffect((): ReturnType<EffectCallback> => {
        props.actions.setMethods(
            'Page4_67_E5',
            {
                handleAddEmployerDetailsClick
            }
        );

        return (): void => {
            props.actions.clearMethods('Page4_67_E5');
        };
    }, [ ]);

    const getContent = () => {

        let preparedEmployeeEmployerEntries = [];
        let trackedIncomeSources: Array<Income> = [];
        let uniqueEmployeeEmployerList = [];

        const uniqueEmployer = new Set();

        householdMembers.map((member: HouseholdMember) => {
            const { incomes, name } = member;

            trackedIncomeSources = incomes
                .filter(
                    (income: Income): boolean =>
                        income.type === IncomeType.JOB || income.type === IncomeType.UNEMPLOYMENT
                );

            trackedIncomeSources.map((income: Income) => uniqueEmployer.add(income.sourceName));

            uniqueEmployeeEmployerList = [
                ...uniqueEmployeeEmployerList,
                {
                    name: normalizeNameOfPerson(name),
                    applicantGuid: member.applicantGuid,
                    sourceName: [...uniqueEmployer]
                }
            ] as any;

            uniqueEmployer.clear();
        });

        const getIsChecked = (employerName: string, applicantGuid: string): boolean => {
            const foundHRA: HraDetails | undefined = healthReimbursementArrangement
                .find((hra: HraDetails) => hra.employerName === employerName && hra.applicantGuid === applicantGuid);
            return !!foundHRA;
        };

        uniqueEmployeeEmployerList.map(usn => {
            const { sourceName, applicantGuid } = usn;
            // @ts-ignore
            sourceName.map(emp => {
                preparedEmployeeEmployerEntries = [
                    ...preparedEmployeeEmployerEntries,
                    {
                        // @ts-ignore
                        memberName: usn.name,
                        applicantGuid: applicantGuid,
                        employerName: emp,
                        isChecked: getIsChecked(emp, applicantGuid)
                    }
                ] as any;
            });
        });

        return preparedEmployeeEmployerEntries.map((entry, idx) => {
            const {
                applicantGuid,
                employerName,
                isChecked,
                memberName,
            } = entry;

            const idxInHra: number = healthReimbursementArrangement.findIndex((hra: HraDetails) => {
                return hra.applicantGuid === applicantGuid && hra.employerName === employerName;
            });

            const {
                cityName,
                enrolledInIchraIndicator,
                employeeSelfOnlyOfferAmount,
                employerPhoneNumber,
                hraType,
                startDate,
                stateCode,
                streetName1,
                streetName2,
                zipCode
            } = ~idxInHra
                ? healthReimbursementArrangement[idxInHra]
                : {
                    cityName: null,
                    enrolledInIchraIndicator: null,
                    employeeSelfOnlyOfferAmount: 0,
                    employerPhoneNumber: null,
                    hraType: null,
                    startDate: null,
                    stateCode: null,
                    streetName1: null,
                    streetName2: null,
                    zipCode: null
                };

            return (
                <li
                    className = 'list__with__btn'
                    key = { `${ applicantGuid }${ employerName }` }
                >
                    <UniversalInput
                        label = { {
                            text: 'common.input.label.non_internationalized_text',
                            labelTextReplacements: {
                                text: (
                                    <span className = 'li__box__label' >
                                        <span className = 'li__box__label_col margin-l-20' >
                                            { employerName }
                                        </span>
                                        <span className = 'li__box__label_col' >
                                            { memberName }
                                        </span>
                                    </span>
                                )
                            },
                            labelClass: [ 'grid-col-12' ]
                        } }
                        inputData = { {
                            isInputRequired: false,
                            classes: [ 'grid-row' ],
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'isChecked',
                            checked: isChecked,
                            value: isChecked,
                            currentData: { isChecked },
                            fields: []
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange_checkbox
                        } }
                        indexInArray = { [ applicantGuid, employerName, idx ] }
                    />

                    {
                        isChecked &&

                        <Fragment>
                            <Fragment>
                                <ButtonContainer
                                    args = { [ householdMembers, entry, idxInHra ] }
                                    type = 'addHraDetailButton'
                                />
                                <InstructionalText
                                    Component = {
                                        <span data-instructions = 'instructional_text.coverage_add_employer' />
                                    }
                                />
                            </Fragment>

                            <div className = 'bordered-b-box' >
                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.phone',
                                        labelClasses: []
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                                        name: 'employerPhoneNumber',
                                        helperClasses: [],
                                        value: employerPhoneNumber,
                                        placeholder: 'ssap.input.placeholder.phone',
                                        validationType: VALIDATION_TYPES.PHONE_NUMBER,
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [ ]
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.address_line_one',
                                        labelClasses: []
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.REGULAR_INPUT,
                                        name: 'streetName1',
                                        helperClasses: [],
                                        value: streetName1,
                                        placeholder: 'ssap.input.placeholder.address_line_one',
                                        validationType: VALIDATION_TYPES.ADDRESS_LINE1,
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [ ]
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.address_line_two',
                                        labelClasses: []
                                    } }
                                    inputData = { {
                                        isInputRequired: false,
                                        type: INPUT_TYPES.REGULAR_INPUT,
                                        name: 'streetName2',
                                        helperClasses: [],
                                        value: streetName2,
                                        placeholder: 'ssap.input.placeholder.address_line_two',
                                        validationType: VALIDATION_TYPES.ADDRESS_LINE2,
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [ ]
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.city',
                                        labelClasses: []
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.REGULAR_INPUT,
                                        name: 'cityName',
                                        helperClasses: [],
                                        value: cityName,
                                        placeholder: 'ssap.input.placeholder.city',
                                        validationType: VALIDATION_TYPES.CITY,
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [ ]
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.zip',
                                        labelClasses: []
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.REGULAR_INPUT,
                                        name: 'zipCode',
                                        helperClasses: [],
                                        value: zipCode,
                                        placeholder: 'ssap.input.placeholder.zip',
                                        validationType: VALIDATION_TYPES.ZIP_CODE_5_DIGITS,
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [ ]
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.state',
                                        isLabelRequired: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.DROPDOWN,
                                        name: 'stateCode',
                                        value: stateCode,
                                        options: DROPDOWN_OPTIONS.STATE,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [ ]
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.enrolled_in_hra',
                                        legendTextReplacements: {
                                            personName: normalizeNameOfPerson(name)
                                        }
                                    } }
                                    label = { {
                                        ulClass: [ 'margin-b-10' ],
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'enrolledInIchraIndicator',
                                        value: enrolledInIchraIndicator,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />

                                <ValidationErrorMessage
                                    isVisible = { isFormSubmitted && isHraErrorMessageShown(idxInHra) }
                                    messageText = { VALIDATION_ERROR_MESSAGES.CHOSEN }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />

                                <InstructionalText
                                    Component = {
                                        <p
                                            data-instructions = 'instructional_text.hra_types' >
                                            { 'ssap.additional_info.hra.type' }
                                        </p>
                                    }
                                    required
                                />

                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.ichra',
                                        labelClass: ['usa-width-one-whole', 'layout--02']
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.CHECKBOX,
                                        classes: [ 'disp-styl' ],
                                        name: 'hraType',
                                        checked: hraType === 'ICHRA',
                                        value: hraType === 'ICHRA',
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [ ],
                                        valueToPass: 'ICHRA'
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.qsehra',
                                        labelClass: ['usa-width-one-whole', 'layout--02']
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.CHECKBOX,
                                        classes: [ 'disp-styl' ],
                                        name: 'hraType',
                                        checked: hraType === 'QSEHRA',
                                        value: hraType === 'QSEHRA',
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [ ],
                                        valueToPass: 'QSEHRA'
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                                <p>
                                    <FormattedMessage id = 'ssap.additional_info.hra_amount'/>
                                </p>

                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.hra_amount',
                                        labelClasses: [ 'tablet:grid-col-4' ]
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.MONEY_INPUT,
                                        name: 'employeeSelfOnlyOfferAmount',
                                        value: employeeSelfOnlyOfferAmount,
                                        isOnlyPositiveAvailable: true,
                                        inputClasses: [ 'tablet:grid-col-6' ],
                                        validationType: VALIDATION_TYPES.MONEY,
                                        placeholder: 'ssap.input.placeholder.amount',
                                        validationErrorMessage: VALIDATION_ERROR_MESSAGES.DOLLAR,
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: []
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                                <p>
                                    <FormattedMessage id = 'ssap.additional_info.hra_start_date'/>
                                </p>

                                <UniversalInput
                                    label = { {
                                        text: 'ssap.input.label.hra_start_date',
                                        labelClass: [ 'tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label' ],
                                    } }
                                    inputData = { {
                                        divClass: [ 'tablet:grid-col-6' ],
                                        isInputRequired: true,
                                        type: INPUT_TYPES.DATE_PICKER,
                                        name: 'startDate',
                                        value: startDate,
                                        valuesToMatch: [ date1, date2 ],
                                        validationType: VALIDATION_TYPES.DATE_BETWEEN_TWO_DATES,
                                        validationErrorMessage: VALIDATION_ERROR_MESSAGES.DATE,
                                        currentData: healthReimbursementArrangement[idxInHra],
                                        fields: [ ]
                                    } }
                                    errorClasses = { [ 'grid-offset-4' ] }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_HraInfo
                                    } }
                                    indexInArray = { idxInHra }
                                />

                            </div>
                        </Fragment>
                    }
                </li>
            );
        });
    };

    const checkTopLevelValidation = (): boolean => {
        if(offeredIchraIndicator === false) {
            return true;
        } else {
            if (healthReimbursementArrangement.length > 0) {
                return healthReimbursementArrangement.every((hra: HraDetails) => !!hra.hraType);
            } else {
                return false;
            }
        }
    };

    const isHraErrorMessageShown = (idxInHra: number): boolean => {
        return !healthReimbursementArrangement[idxInHra].hraType;
    };

    const isEmployerErrorMessageShown = (): boolean => {
        switch(offeredIchraIndicator) {
            case false:
            case null:
                return false;
            case true:
                return healthReimbursementArrangement.length === 0;
        }
    };

    return (
        <Form
            topLevelValidity = { checkTopLevelValidation() }
            { ...{ formName } }
        >
            <div
                className = 'fade-in'
                id = 'aiHealthReimbursementArrangementDetail'
            >
                <div className = 'subsection' >
                    <ValidationErrorMessage
                        isVisible = { isFormSubmitted && isEmployerErrorMessageShown() }
                        messageText = { VALIDATION_ERROR_MESSAGES.EMPLOYER_HRA }
                        errorClasses = { [ 'margin-l-0' ] }
                    />

                    <UniversalInput
                        legend = { {
                            legendText: 'ssap.input.legend.hra_through_job',
                            legendTextReplacements: {
                                date1,
                                date2,
                                personName: (
                                    <strong>
                                        { normalizeNameOfPerson(name) }
                                    </strong>
                                )
                            }
                        } }
                        label = { {
                            showRequiredIcon: true
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.RADIO_BUTTONS,
                            name: 'offeredIchraIndicator',
                            value: offeredIchraIndicator,
                            options: DROPDOWN_OPTIONS.YES_NO,
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: householdMembers[member],
                            fields: [ 'healthCoverage' ]
                        } }
                        inputActions = { {
                            onFieldChange: onFieldChange
                        } }
                        errorClasses = { [ 'margin-l-0' ] }
                    />

                    {
                        isHraExtended &&

                        <div className = 'grid-row'>
                            {/* TODO: Check with Tejal: li__box class name was removed in fieldset below */}
                            <fieldset className = 'grid-col-12 usa-fieldset' >
                                <legend className = 'li__box-legend' >
                                    <span className = 'li__box-legend-header' >
                                        <FormattedMessage
                                            id = 'ssap.additional_info.employer_coverage.employer_name'
                                            tagName = 'strong'
                                        />
                                    </span>
                                    <span className = 'li__box-legend-header margin-l-15' >
                                        <FormattedMessage
                                            id = 'ssap.additional_info.employer_coverage.employee_name'
                                            tagName = 'strong'
                                        />
                                    </span>
                                </legend>
                                <ul className = 'usa-list usa-list--unstyled' >
                                    {
                                        getContent()
                                    }
                                </ul>
                            </fieldset>
                        </div>
                    }

                </div>
            </div>
        </Form>
    );
};

const mapStateToProps = (state: ApplicationStore): Partial<SsapPage> => ({
    environment: state.common.environment,

    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    page: state.ssap.page
});

const mapDispatchToProps = (dispatch: Dispatch): { actions: Partial<SsapReduxActions> } => ({
    actions: bindActionCreators({
        changeData,
        clearMethods,
        setMethods,
        showModal
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page4_67_E5);